import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  X,
  ArrowLeftRight,
  ArrowRight,
  Languages,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  Calendar,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface Synonym {
  id: string
  term: string
  synonym: string
  isBidirectional: boolean
  notes: string | null
  hits: number
  createdAt: string | null
  updatedAt: string | null
}

interface SynonymsData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: Synonym[]
}

interface TopQueryRow {
  query: string
  count: number
  avgResults: number
  conversionRate?: number | null
}

interface TopQueriesResponse {
  from?: string
  to?: string
  days: number
  total: number
  unique: number
  noResultsCount: number
  noResultsRate: number
  data: TopQueryRow[]
}

interface NoResultsRow {
  query: string
  count: number
  lastSeenAt: string | null
}

interface NoResultsResponse {
  days: number
  total: number
  data: NoResultsRow[]
}

interface ByDayRow {
  d: string
  count: number
}

interface ByDayResponse {
  days: number
  data: ByDayRow[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabs config
// ─────────────────────────────────────────────────────────────────────────────

type TabId = 'sinonimos' | 'top' | 'sin-resultados'

interface TabDef {
  id: TabId
  label: string
  icon: typeof Languages
}

const TABS: TabDef[] = [
  { id: 'sinonimos', label: 'Sinónimos', icon: Languages },
  { id: 'top', label: 'Top búsquedas', icon: TrendingUp },
  { id: 'sin-resultados', label: 'Sin resultados', icon: AlertTriangle },
]

// ─────────────────────────────────────────────────────────────────────────────
// Página principal
// ─────────────────────────────────────────────────────────────────────────────

interface PrefillSynonym {
  term: string
  synonym: string
}

export function Buscador() {
  const [activeTab, setActiveTab] = useState<TabId>('sinonimos')

  // Estado para que la tab "Sin resultados" pueda mandar un término a "Sinónimos"
  // y abrir directamente el modal de creación pre-llenado.
  const [prefill, setPrefill] = useState<PrefillSynonym | null>(null)

  function jumpToSinonimosWith(term: string) {
    setPrefill({ term, synonym: '' })
    setActiveTab('sinonimos')
  }

  return (
    <>
      <Topbar
        title="Buscador"
        subtitle="Sinónimos, top de búsquedas y oportunidades sin resultados"
      />
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap gap-1 rounded-xl border border-ink-200 bg-white p-1">
          {TABS.map((t) => {
            const Icon = t.icon
            const active = t.id === activeTab
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active ? 'bg-brand-600 text-white' : 'text-ink-700 hover:bg-ink-50'
                }`}
              >
                <Icon className="size-4" />
                {t.label}
              </button>
            )
          })}
        </div>

        {activeTab === 'sinonimos' && (
          <SinonimosTab prefill={prefill} clearPrefill={() => setPrefill(null)} />
        )}
        {activeTab === 'top' && <TopBusquedasTab />}
        {activeTab === 'sin-resultados' && (
          <SinResultadosTab onCreateSynonym={jumpToSinonimosWith} />
        )}
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers comunes
// ─────────────────────────────────────────────────────────────────────────────

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

function formatDateShort(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
    })
  } catch {
    return iso
  }
}

function StateView({
  isLoading,
  error,
  empty,
  emptyMessage,
  children,
}: {
  isLoading: boolean
  error: unknown
  empty?: boolean
  emptyMessage?: string
  children: React.ReactNode
}) {
  if (isLoading) {
    return (
      <div className="grid place-items-center rounded-2xl border border-ink-200 bg-white py-20 text-ink-500">
        <Loader2 className="size-8 animate-spin" />
        <p className="mt-3 text-sm">Cargando…</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {(error as Error).message}
      </div>
    )
  }
  if (empty) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-10 text-center text-sm text-ink-500">
        {emptyMessage ?? 'Sin datos.'}
      </div>
    )
  }
  return <>{children}</>
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1: SINÓNIMOS
// ─────────────────────────────────────────────────────────────────────────────

interface SynonymForm {
  term: string
  synonym: string
  isBidirectional: boolean
  notes: string
}

const EMPTY_SYNONYM_FORM: SynonymForm = {
  term: '',
  synonym: '',
  isBidirectional: true,
  notes: '',
}

function SinonimosTab({
  prefill,
  clearPrefill,
}: {
  prefill: PrefillSynonym | null
  clearPrefill: () => void
}) {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<SynonymForm>(EMPTY_SYNONYM_FORM)
  const [formError, setFormError] = useState('')

  // Si llega un prefill desde la tab "Sin resultados", abrimos modal nuevo.
  useEffect(() => {
    if (prefill) {
      setEditingId(null)
      setForm({
        ...EMPTY_SYNONYM_FORM,
        term: prefill.term,
        synonym: prefill.synonym,
      })
      setFormError('')
      setModalOpen(true)
      clearPrefill()
    }
  }, [prefill, clearPrefill])

  const params = new URLSearchParams()
  params.set('page', String(page))
  if (searchTerm) params.set('q', searchTerm)

  const { data, isLoading, isFetching, error } = useQuery<SynonymsData>({
    queryKey: ['search-synonyms', page, searchTerm],
    queryFn: () => apiGet(`/api/admin/search/synonyms?${params.toString()}`),
    placeholderData: (prev) => prev,
  })

  const createMut = useMutation({
    mutationFn: (body: SynonymForm) =>
      apiPost('/api/admin/search/synonyms', {
        term: body.term.trim(),
        synonym: body.synonym.trim(),
        isBidirectional: body.isBidirectional,
        notes: body.notes.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['search-synonyms'] })
      closeModal()
    },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: SynonymForm }) =>
      apiPut(`/api/admin/search/synonyms/${args.id}`, {
        term: args.body.term.trim(),
        synonym: args.body.synonym.trim(),
        isBidirectional: args.body.isBidirectional,
        notes: args.body.notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['search-synonyms'] })
      closeModal()
    },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/search/synonyms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['search-synonyms'] }),
    onError: (e: Error) => alert(e.message),
  })

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  function openNew() {
    setEditingId(null)
    setForm(EMPTY_SYNONYM_FORM)
    setFormError('')
    setModalOpen(true)
  }

  function openEdit(row: Synonym) {
    setEditingId(row.id)
    setForm({
      term: row.term,
      synonym: row.synonym,
      isBidirectional: row.isBidirectional,
      notes: row.notes ?? '',
    })
    setFormError('')
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingId(null)
    setForm(EMPTY_SYNONYM_FORM)
    setFormError('')
  }

  function onDelete(row: Synonym) {
    if (
      window.confirm(
        `¿Eliminar el sinónimo "${row.term} ${row.isBidirectional ? '↔' : '→'} ${row.synonym}"?`,
      )
    ) {
      deleteMut.mutate(row.id)
    }
  }

  function submitForm(e: React.FormEvent) {
    e.preventDefault()
    setFormError('')
    if (!form.term.trim()) return setFormError('El término es requerido')
    if (!form.synonym.trim()) return setFormError('El sinónimo es requerido')
    if (form.term.trim().toLowerCase() === form.synonym.trim().toLowerCase()) {
      return setFormError('Término y sinónimo deben ser distintos')
    }

    if (editingId) {
      updateMut.mutate({ id: editingId, body: form })
    } else {
      createMut.mutate(form)
    }
  }

  const isSaving = createMut.isPending || updateMut.isPending

  return (
    <div className="space-y-4">
      {/* Hint */}
      <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
        <Lightbulb className="size-4 shrink-0 text-sky-600" />
        <div>
          Cuando un cliente busca el <strong>término</strong>, también se muestran
          productos del <strong>sinónimo</strong> (y al revés si está marcado como
          bidireccional). Ej: <code className="font-mono">pastillas ↔ balatas</code>,{' '}
          <code className="font-mono">amortiguador ↔ muelle</code>.
        </div>
      </div>

      {/* Topbar de tab: search + nuevo */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <form onSubmit={onSearchSubmit} className="flex flex-1 flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por término o sinónimo…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
        </form>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          <Plus className="size-3.5" /> Agregar sinónimo
        </button>
      </div>

      {/* Tabla */}
      <StateView isLoading={isLoading && !data} error={error}>
        {data && (
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <div className="relative overflow-x-auto">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">Término</th>
                    <th className="w-10 px-2 py-3"></th>
                    <th className="px-4 py-3 font-medium">Sinónimo</th>
                    <th className="px-4 py-3 font-medium">Dirección</th>
                    <th className="w-20 px-4 py-3 text-right font-medium">Hits</th>
                    <th className="px-4 py-3 font-medium">Notas</th>
                    <th className="w-24 px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-ink-500">
                        Sin sinónimos. Agregá el primero con el botón de arriba.
                      </td>
                    </tr>
                  )}
                  {data.data.map((s) => (
                    <tr key={s.id} className="border-t border-ink-100 hover:bg-ink-50">
                      <td className="px-4 py-3 font-medium text-ink-900">{s.term}</td>
                      <td className="px-2 py-3 text-center text-ink-400">
                        {s.isBidirectional ? (
                          <ArrowLeftRight className="size-4" />
                        ) : (
                          <ArrowRight className="size-4" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-900">{s.synonym}</td>
                      <td className="px-4 py-3">
                        {s.isBidirectional ? (
                          <Badge variant="info">Bidireccional</Badge>
                        ) : (
                          <Badge>Unidireccional</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-ink-700">
                        {s.hits.toLocaleString('es-MX')}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-500 line-clamp-2">
                        {s.notes || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(s)}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            title="Editar"
                          >
                            <Edit className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(s)}
                            disabled={deleteMut.isPending}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                            title="Eliminar"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
              <div className="text-ink-500">
                Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong> (
                {data.total.toLocaleString('es-MX')} sinónimos)
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.pages}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </StateView>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
              <h2 className="text-base font-semibold text-ink-900">
                {editingId ? 'Editar sinónimo' : 'Nuevo sinónimo'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1 text-ink-500 hover:bg-ink-100"
              >
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={submitForm} className="space-y-3 px-5 py-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-700">
                  Término
                </label>
                <input
                  type="text"
                  required
                  value={form.term}
                  onChange={(e) => setForm({ ...form, term: e.target.value })}
                  placeholder="pastillas"
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <p className="mt-1 text-[11px] text-ink-500">
                  Cómo lo escribe el cliente al buscar.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-700">
                  Sinónimo
                </label>
                <input
                  type="text"
                  required
                  value={form.synonym}
                  onChange={(e) => setForm({ ...form, synonym: e.target.value })}
                  placeholder="balatas"
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <p className="mt-1 text-[11px] text-ink-500">
                  Equivalente que también queremos mostrar.
                </p>
              </div>
              <label className="flex items-start gap-2 rounded-lg border border-ink-200 bg-ink-50/40 p-3 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={form.isBidirectional}
                  onChange={(e) =>
                    setForm({ ...form, isBidirectional: e.target.checked })
                  }
                  className="mt-0.5"
                />
                <span>
                  <strong>Bidireccional</strong> — buscar cualquiera muestra ambos
                  resultados. Si se desactiva, sólo el término encuentra al sinónimo
                  (no al revés).
                </span>
              </label>
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-700">
                  Notas (opcional)
                </label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ej: pidieron muchos clientes desde el call center"
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>

              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="flex justify-end gap-2 border-t border-ink-100 pt-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                >
                  {isSaving && <Loader2 className="size-4 animate-spin" />}
                  {editingId ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2: TOP BÚSQUEDAS
// ─────────────────────────────────────────────────────────────────────────────

type PeriodDays = 7 | 30 | 90

function TopBusquedasTab() {
  const [days, setDays] = useState<PeriodDays>(30)

  const top = useQuery<TopQueriesResponse>({
    queryKey: ['search-top', days],
    queryFn: () =>
      apiGet(`/api/admin/search/analytics/top?days=${days}&limit=50`),
  })

  const byDay = useQuery<ByDayResponse>({
    queryKey: ['search-by-day', days],
    queryFn: () => apiGet(`/api/admin/search/analytics/by-day?days=${days}`),
  })

  return (
    <div className="space-y-4">
      {/* Selector de período */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink-900">Análisis de búsquedas</h3>
        <div className="inline-flex rounded-lg border border-ink-200 bg-white p-1 text-sm">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d as PeriodDays)}
              className={`rounded-md px-3 py-1 font-medium ${
                days === d ? 'bg-brand-600 text-white' : 'text-ink-700 hover:bg-ink-50'
              }`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label={`Búsquedas (${days}d)`}
          value={top.data ? top.data.total.toLocaleString('es-MX') : '—'}
          color="blue"
        />
        <StatCard
          label="Términos únicos"
          value={top.data ? top.data.unique.toLocaleString('es-MX') : '—'}
          color="violet"
        />
        <StatCard
          label="% sin resultados"
          value={top.data ? `${(top.data.noResultsRate * 100).toFixed(1)}%` : '—'}
          color={
            top.data && top.data.noResultsRate > 0.1 ? 'red' : 'emerald'
          }
          hint={
            top.data
              ? `${top.data.noResultsCount.toLocaleString('es-MX')} queries con 0 resultados`
              : undefined
          }
        />
      </div>

      {/* Gráfica búsquedas por día */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-ink-900">Búsquedas por día</h2>
            <p className="text-xs text-ink-500">Últimos {days} días</p>
          </div>
        </div>
        {byDay.isLoading ? (
          <div className="grid h-20 place-items-center text-ink-400">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : byDay.data && byDay.data.data.length > 0 ? (
          <>
            <SearchSparkline data={byDay.data.data} />
            <div className="mt-2 flex justify-between text-[10px] text-ink-400">
              <span>{formatDateShort(byDay.data.data[0].d)}</span>
              <span>
                {formatDateShort(byDay.data.data[byDay.data.data.length - 1].d)}
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-ink-500">Sin datos en el período.</p>
        )}
      </div>

      {/* Tabla top */}
      <StateView
        isLoading={top.isLoading}
        error={top.error}
        empty={top.data && top.data.data.length === 0}
        emptyMessage="Aún no hay búsquedas registradas."
      >
        {top.data && (
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <header className="border-b border-ink-200 px-5 py-3">
              <h3 className="text-sm font-semibold text-ink-900">
                Top 50 términos más buscados
              </h3>
              <p className="text-[11px] text-ink-500">
                Ordenado por # de veces buscado (descendente)
              </p>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="w-12 px-4 py-3 text-center font-medium">#</th>
                    <th className="px-4 py-3 font-medium">Búsqueda</th>
                    <th className="w-28 px-4 py-3 text-right font-medium">Veces</th>
                    <th className="w-32 px-4 py-3 text-right font-medium">
                      Resultados (prom.)
                    </th>
                    <th className="w-32 px-4 py-3 text-right font-medium">
                      Conversión
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {top.data.data.map((row, i) => (
                    <tr
                      key={row.query}
                      className="border-t border-ink-100 hover:bg-ink-50"
                    >
                      <td className="px-4 py-3 text-center">
                        <span className="grid size-6 place-items-center rounded-md bg-brand-50 text-[10px] font-bold text-brand-700">
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-ink-900">{row.query}</td>
                      <td className="px-4 py-3 text-right font-semibold text-ink-900">
                        {row.count.toLocaleString('es-MX')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.avgResults <= 0 ? (
                          <Badge variant="danger">0</Badge>
                        ) : row.avgResults < 3 ? (
                          <Badge variant="warning">
                            {row.avgResults.toFixed(1)}
                          </Badge>
                        ) : (
                          <span className="text-ink-700">
                            {row.avgResults.toFixed(1)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-ink-600">
                        {row.conversionRate != null
                          ? `${(row.conversionRate * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </StateView>
    </div>
  )
}

function SearchSparkline({ data }: { data: ByDayRow[] }) {
  if (!data.length) return null
  const max = Math.max(...data.map((d) => d.count), 1)
  const W = 600
  const H = 80
  const stepX = W / (data.length - 1 || 1)
  const points = data.map((d, i) => {
    const x = i * stepX
    const y = H - (d.count / max) * (H - 8) - 4
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const path = `M ${points.join(' L ')}`
  const area = `${path} L ${W},${H} L 0,${H} Z`
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-20 w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="searchSparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E85230" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#E85230" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#searchSparkGrad)" />
      <path
        d={path}
        fill="none"
        stroke="#E85230"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3: SIN RESULTADOS
// ─────────────────────────────────────────────────────────────────────────────

function SinResultadosTab({
  onCreateSynonym,
}: {
  onCreateSynonym: (term: string) => void
}) {
  const [days, setDays] = useState<PeriodDays>(30)

  const { data, isLoading, error } = useQuery<NoResultsResponse>({
    queryKey: ['search-no-results', days],
    queryFn: () =>
      apiGet(`/api/admin/search/analytics/no-results?days=${days}&limit=50`),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <Lightbulb className="size-4 shrink-0 text-amber-600" />
        <div>
          <strong>Oro para el negocio.</strong> Estas son búsquedas que devolvieron 0
          resultados — demanda de productos que no tienes, o nombres alternos que la
          gente usa para productos que sí vendes. Asocia un sinónimo o agrega el
          producto al catálogo.
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink-900">
          Búsquedas sin resultados
        </h3>
        <div className="inline-flex rounded-lg border border-ink-200 bg-white p-1 text-sm">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d as PeriodDays)}
              className={`rounded-md px-3 py-1 font-medium ${
                days === d ? 'bg-brand-600 text-white' : 'text-ink-700 hover:bg-ink-50'
              }`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      <StateView
        isLoading={isLoading}
        error={error}
        empty={data && data.data.length === 0}
        emptyMessage="Ninguna búsqueda devolvió 0 resultados en este período. ¡Buen catálogo!"
      >
        {data && (
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <header className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">
                  Top {data.data.length} oportunidades
                </h3>
                <p className="text-[11px] text-ink-500">
                  {data.total.toLocaleString('es-MX')} queries únicas sin resultados
                  en los últimos {data.days} días
                </p>
              </div>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="w-12 px-4 py-3 text-center font-medium">#</th>
                    <th className="px-4 py-3 font-medium">Búsqueda</th>
                    <th className="w-24 px-4 py-3 text-right font-medium">
                      Veces
                    </th>
                    <th className="w-44 px-4 py-3 font-medium">Último intento</th>
                    <th className="w-44 px-4 py-3 text-right font-medium">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((row, i) => (
                    <tr
                      key={row.query}
                      className="border-t border-ink-100 hover:bg-ink-50"
                    >
                      <td className="px-4 py-3 text-center">
                        <span className="grid size-6 place-items-center rounded-md bg-amber-100 text-[10px] font-bold text-amber-700">
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-ink-900">{row.query}</td>
                      <td className="px-4 py-3 text-right">
                        <Badge variant="danger">
                          {row.count.toLocaleString('es-MX')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-500">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="size-3 text-ink-400" />
                          {formatDateTime(row.lastSeenAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => onCreateSynonym(row.query)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                        >
                          <Plus className="size-3" /> Crear sinónimo
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </StateView>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Componentes reusables
// ─────────────────────────────────────────────────────────────────────────────

type StatColor = 'emerald' | 'blue' | 'violet' | 'amber' | 'red'

function StatCard({
  label,
  value,
  color,
  hint,
}: {
  label: string
  value: string | number
  color: StatColor
  hint?: string
}) {
  const palette: Record<StatColor, string> = {
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    violet: 'text-violet-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
  }
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <div className="text-xs font-medium text-ink-500">{label}</div>
      <div className={`mt-1 text-2xl font-extrabold ${palette[color]}`}>{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-ink-500">{hint}</div>}
    </div>
  )
}

export default Buscador
